import React, { useEffect, useRef, useState } from 'react';
import { INLINE_CTAS } from '@/content/resources/ctas';
import { FIGURE_SCROLL_MIN_WIDTH, standaloneSvg } from './figureScroll.js';

// Components available inside content/resources/*.mdx (see mdxComponents.js
// and content/resources/README.md).

const CALLOUT_TITLES = { note: 'Note', example: 'Example', warning: 'Warning' };

export const Callout = ({ kind = 'note', title, children }) => (
  <aside className={`rs-callout rs-callout-${CALLOUT_TITLES[kind] ? kind : 'note'}`} role="note">
    <p className="rs-callout-title">{title || CALLOUT_TITLES[kind] || 'Note'}</p>
    {children}
  </aside>
);

// Semantic table in a scroll container with a sticky header. Plain markdown
// tables render through the same wrapper (ProseTable).
export const ProseTable = (props) => (
  <div className="rs-table-wrap" tabIndex={0} role="region" aria-label="Table">
    <table {...props} />
  </div>
);

const formatKeyDate = (iso) => {
  const d = new Date(`${iso}T00:00:00Z`);
  return Number.isNaN(d.getTime()) ? iso : d.toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric', timeZone: 'UTC' });
};

const ISO_DATE = /^\d{4}-\d{2}-\d{2}$/;

// Two-column table with a fixed-width date column. ISO dates are formatted and
// machine-readable; free text ("25 May 2018") is shown as written.
export const KeyDates = ({ rows, caption = 'Key dates' }) => (
  <div className="rs-table-wrap" tabIndex={0} role="region" aria-label={caption}>
    <table className="rs-dates">
      <colgroup>
        <col className="rs-dates-date" />
        <col />
      </colgroup>
      <thead>
        <tr>
          <th scope="col">Date</th>
          <th scope="col">What happened</th>
        </tr>
      </thead>
      <tbody>
        {rows.map((r) => (
          <tr key={`${r.date}-${r.what}`}>
            <th scope="row">{ISO_DATE.test(r.date) ? <time dateTime={r.date}>{formatKeyDate(r.date)}</time> : r.date}</th>
            <td>{r.what}</td>
          </tr>
        ))}
      </tbody>
    </table>
  </div>
);

// A sentence with a link on a light tint, not a card or a button. Variants
// in src/content/resources/ctas.js (INLINE_CTAS).
export const InlineCta = ({ variant = 'assessment' }) => {
  const c = INLINE_CTAS[variant];
  if (!c) throw new Error(`InlineCta: unknown variant "${variant}"`);
  return (
    <p className="rs-inline-cta">
      {c.text} <a href={c.href}>{c.link}<span aria-hidden="true"> →</span></a>
    </p>
  );
};

export const Cite = ({ n }) => (
  <sup className="rs-cite">
    <a href={`#source-${n}`} aria-label={`Source ${n}`}>[{n}]</a>
  </sup>
);

// <figure> around a local image (public/resources/img/...) or a React
// component passed as children (inline SVG from src/content/figures/).
// wide (default): on desktop the figure runs from the text column's left edge
// to the rail's right edge; the rail moves out of its way (StickyRail). A
// component figure with real text and no `alt` stays readable as text;
// with `alt` it is announced as one image.
// Inline-SVG figures. Below 640px the SVG sits in a horizontally scrollable
// container at FIGURE_SCROLL_MIN_WIDTH (so no text renders under 11px), with
// a "Scroll →" hint until the first scroll and a "View full size" link that
// opens the SVG alone in a new tab. From 640px the figure fits its column as
// before. See figureScroll.js.
// If the figure marks an element with data-scroll-center, a scrollable figure
// opens scrolled so that element is centred; that scroll keeps the hint.
// `legend` renders inside the frame, below the scroll area, so it stays in
// view on phones.
const ScrollFigure = ({ alt, legend, children }) => {
  const scrollRef = useRef(null);
  const ignoreScroll = useRef(false);
  const [scrolled, setScrolled] = useState(false);
  useEffect(() => {
    const box = scrollRef.current;
    const target = box?.querySelector('[data-scroll-center]');
    if (!box || !target || box.scrollWidth <= box.clientWidth) return;
    const b = box.getBoundingClientRect();
    const t = target.getBoundingClientRect();
    const left = Math.round(box.scrollLeft + (t.left - b.left) + t.width / 2 - box.clientWidth / 2);
    const next = Math.max(0, Math.min(left, box.scrollWidth - box.clientWidth));
    if (next === box.scrollLeft) return;
    ignoreScroll.current = true;
    box.scrollLeft = next;
    const clear = setTimeout(() => { ignoreScroll.current = false; }, 300);
    return () => clearTimeout(clear);
  }, []);
  const onScroll = () => {
    if (ignoreScroll.current) { ignoreScroll.current = false; return; }
    setScrolled(true);
  };
  return (
    <div className="rs-figure-frame rs-figure-frame-scroll" {...(alt ? { role: 'img', 'aria-label': alt } : {})}>
      <div
        ref={scrollRef}
        className="rs-figure-scroll"
        tabIndex={0}
        role="region"
        aria-label="Figure, scrolls sideways on small screens"
        onScroll={scrolled ? undefined : onScroll}
      >
        <div className="rs-figure-scroll-inner" style={{ '--rs-fig-min': `${FIGURE_SCROLL_MIN_WIDTH}px` }}>{children}</div>
      </div>
      {!scrolled && <span className="rs-figure-hint" aria-hidden="true">Scroll →</span>}
      {legend}
    </div>
  );
};

const FullSizeLink = ({ figureRef }) => {
  const [href, setHref] = useState(null);
  useEffect(() => {
    const svg = figureRef.current?.querySelector('.rs-figure-scroll svg');
    if (!svg || typeof XMLSerializer === 'undefined' || typeof URL.createObjectURL !== 'function') return undefined;
    const markup = standaloneSvg(new XMLSerializer().serializeToString(svg), getComputedStyle(svg).fontFamily);
    const url = URL.createObjectURL(new Blob([markup], { type: 'image/svg+xml' }));
    setHref(url);
    return () => URL.revokeObjectURL(url);
  }, [figureRef]);
  if (!href) return null;
  return <a className="rs-figure-full" href={href} target="_blank" rel="noopener">View full size</a>;
};

export const Infographic = ({ src, alt, caption, width, height, wide = true, legend, children }) => {
  const figureRef = useRef(null);
  return (
    <figure ref={figureRef} className={wide ? 'rs-figure rs-figure-wide' : 'rs-figure'}>
      {src ? (
        <div className="rs-figure-frame">
          <img src={src} alt={alt} width={width} height={height} loading="lazy" decoding="async" />
        </div>
      ) : (
        <ScrollFigure alt={alt} legend={legend}>{children}</ScrollFigure>
      )}
      {caption && <figcaption>{caption}</figcaption>}
      {!src && <FullSizeLink figureRef={figureRef} />}
    </figure>
  );
};
